import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { NavigationService } from 'src/app/services/navigation.service';
import { ScreenType } from 'src/app/enums/enums';
import { firstValueFrom, Observable, take } from 'rxjs';
import { PickupData } from 'src/app/models/pickup/pickup-data.model';
import { PickupService } from 'src/app/services/pickup.service';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ErrorMsg } from 'src/app/models/error/error-msg.model';
import { ShippingPointFormData } from 'src/app/models/shipping-courier/shipping-point-form-data.model';
import { FormService } from 'src/app/services/form.service';
import { HttpErrorResponse } from '@angular/common/http';
import { ErrorResponse } from 'src/app/models/error/error-response.model';
import { ApiService } from 'src/app/services/api.service';
import { SubmitButtonComponent } from '../../../common/submit-button/submit-button.component';
import { BackButtonComponent } from '../../../common/back-button/back-button.component';
import { FormInputControlComponent } from '../../../common/form-input-control/form-input-control.component';
import {
  emailValidator,
  phoneValidator,
} from 'src/app/common/validatos/validators';
import { CaptchaDialogService } from 'src/app/services/captcha-dialog.service';
import { FormErrorMessageComponent } from '../../../common/form-error-message/form-error-message.component';
import { StoreService } from 'src/app/services/store.service';
import { FormPointInputFieldComponent } from '../../../common/form-point-input-field/form-point-input-field.component';
import { PointDto } from 'src/app/models/fds2-points/fds2-points.model';
import { FormHeaderComponent } from '../../../common/form-header/form-header.component';
import { Store } from '@ngrx/store';
import { selectSelectedPartners } from 'src/app/store/forms-data/forms-data.selectors';
import { changeSelectedPartners } from 'src/app/store/forms-data/forms-data.actions';

@Component({
  selector: 'app-shipping-point-form',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    SubmitButtonComponent,
    BackButtonComponent,
    FormInputControlComponent,
    FormErrorMessageComponent,
    FormPointInputFieldComponent,
    FormHeaderComponent,
  ],
  templateUrl: './shipping-point-form.component.html',
  styleUrl: './shipping-point-form.component.scss',
})
export class ShippingPointFormComponent implements AfterViewInit {
  pointForm!: FormGroup;
  submitException = signal(false);
  isSubmitLoading = signal(false);
  selectedPoint: PointDto | undefined;
  selectedPartners$: Observable<string[]>;

  partners = [
    { name: 'ABC', image: 'assets/images/abc.png' },
    { name: 'Delikatesy', image: 'assets/images/delikatesy.png' },
    { name: 'Duży Ben', image: 'assets/images/duzyben.png' },
    { name: 'Groszek', image: 'assets/images/groszek.png' },
    { name: 'Shell', image: 'assets/images/shell1.png' },
    { name: 'Stokrotka', image: 'assets/images/stokrotka.png' },
    { name: 'Żabka', image: 'assets/images/zabka.png' },
    { name: 'Epaka', image: 'assets/images/epaka.png' },
    { name: 'Furgonetka', image: 'assets/images/furgonetka.png' },
    { name: 'Kolporter', image: 'assets/images/kolporter3.png' },
  ];

  constructor(
    private navigationService: NavigationService,
    private pickupService: PickupService,
    private apiService: ApiService,
    private fb: FormBuilder,
    private cdr: ChangeDetectorRef,
    private captchaDialogService: CaptchaDialogService,
    protected formService: FormService,
    protected storeService: StoreService,
    private store: Store
  ) {
    this.initializeForm();
    this.selectedPartners$ = this.store.select(selectSelectedPartners);
  }

  // Gettery dla pól formularza
  get name() {
    return this.pointForm.get('name');
  }
  get company() {
    return this.pointForm.get('company');
  }
  get zipCode() {
    return this.pointForm.get('zip_code');
  }
  get city() {
    return this.pointForm.get('city');
  }
  get street() {
    return this.pointForm.get('street');
  }
  get houseNumber() {
    return this.pointForm.get('house_number');
  }
  get apartmentNumber() {
    return this.pointForm.get('apartment_number');
  }
  get phone() {
    return this.pointForm.get('phone');
  }
  get email() {
    return this.pointForm.get('email');
  }
  get labelEmail() {
    return this.pointForm.get('label-email');
  }

  get pointName() {
    return this.pointForm.get('pointName');
  }

  get pointId() {
    return this.pointForm.get('pointId');
  }

  get pointZipCode() {
    return this.pointForm.get('pointZipCode');
  }

  get pointPartner() {
    return this.pointForm.get('pointPartner');
  }

  get pointCity() {
    return this.pointForm.get('pointCity');
  }

  get pointAddress() {
    return this.pointForm.get('pointAddress');
  }

  isPayerNoProvided(): boolean {
    return this.storeService.getPayerNo() ? true : false;
  }

  isControlTouchedAndInvalid(control: AbstractControl | null) {
    return control?.invalid && control?.touched;
  }

  private initializeFormControl(valueFromData: any, validators: any[] = []) {
    return this.fb.control(valueFromData, validators);
  }

  private initializeForm() {
    const formData = this.storeService.getShippingPointFormData();

    if (this.isPayerNoProvided()) {
      this.pointForm = this.fb.group({
        name: this.initializeFormControl(formData?.name, [Validators.required]),
        company: this.initializeFormControl(formData?.company),
        zip_code: this.initializeFormControl(formData?.zipCode, [
          Validators.required,
          Validators.pattern('^[0-9]{2}-[0-9]{3}$'),
        ]),
        city: this.initializeFormControl(formData?.city, [Validators.required]),
        street: this.initializeFormControl(formData?.street, [
          Validators.required,
        ]),
        house_number: this.initializeFormControl(formData?.houseNumber, [
          Validators.required,
        ]),
        apartment_number: this.initializeFormControl(formData?.apartmentNumber),
        phone: this.initializeFormControl(formData?.phone, [
          Validators.required,
          phoneValidator(),
        ]),
        email: this.initializeFormControl(formData?.email, [
          Validators.required,
          emailValidator(),
        ]),
        pointName: [formData?.pointName, [Validators.required]],
        pointId: [
          { value: formData?.pointId, disabled: true },
          [Validators.required],
        ],
        pointZipCode: [{ value: formData?.pointZipCode, disabled: true }],
        pointPartner: [{ value: formData?.pointPartner, disabled: true }],
        pointCity: [{ value: formData?.pointCity, disabled: true }],
        pointAddress: [{ value: formData?.pointAddress, disabled: true }],
      });
    } else {
      this.pointForm = this.fb.group({
        pointName: [formData?.pointName, [Validators.required]],
        pointId: [
          { value: formData?.pointId, disabled: true },
          [Validators.required],
        ],
        pointZipCode: [{ value: formData?.pointZipCode, disabled: true }],
        pointPartner: [{ value: formData?.pointPartner, disabled: true }],
        pointCity: [{ value: formData?.pointCity, disabled: true }],
        pointAddress: [{ value: formData?.pointAddress, disabled: true }],
      });
    }

    if (!this.storeService.getSender()) {
      this.pointForm.addControl(
        'label-email',
        this.initializeFormControl(formData?.emailForLabel, [emailValidator()])
      );
    }

    this.pointForm.valueChanges.subscribe(() => this.saveFormDataToCache());
    this.zipCode?.valueChanges.subscribe(() => {
      this.city?.updateValueAndValidity({ emitEvent: false });
    });

    this.city?.valueChanges.subscribe(() => {
      this.zipCode?.updateValueAndValidity({ emitEvent: false });
    });
  }

  ngAfterViewInit() {
    const errorResponse = this.storeService.getErrorResponse();
    let errorMsgCopy: ErrorMsg[] | undefined = undefined;

    if (errorResponse) {
      errorMsgCopy = errorResponse?.errorMsg?.filter((err) => {
        let control: any;
        if (err.field === 'name' && err.idRow === 1) {
          control = this.pointForm.get('name');
        } else if (err.field === 'companyName' && err.idRow === 1) {
          control = this.pointForm.get('company');
        } else if (err.field === 'city' && err.idRow === 1) {
          control = this.pointForm.get('city');
        } else if (err.field === 'postalCode' && err.idRow === 1) {
          control = this.pointForm.get('zip_code');
        } else if (err.field === 'address1' && err.idRow === 1) {
          control = this.pointForm.get('street');
        } else if (err.field === 'phone' && err.idRow === 1) {
          control = this.pointForm.get('phone');
        } else if (err.field === 'email' && err.idRow === 1) {
          control = this.pointForm.get('email');
        } else if (err.field === 'ext2Id' && err.idRow === 1) {
          control = this.pointName;
        } else if (err.field === 'emailForLabel') {
          control = this.labelEmail;
        }

        if (control) {
          control.setErrors({ [err.errorCode]: true });
          control.markAsTouched();
          return false; // Usuwa err z listy errorMsg
        }

        return true; // Zatrzymuje err w liście errorMsg
      });
    }

    const errorRespCpy: ErrorResponse = {
      httpStatus: errorResponse?.httpStatus,
      errorMsg: errorMsgCopy,
    };

    this.storeService.setPickupErrorResponse(errorRespCpy);
    this.cdr.detectChanges();
  }

  saveFormDataToCache(): void {
    const shippingPointFormData: ShippingPointFormData = {
      name: this.name?.value,
      company: this.company?.value,
      zipCode: this.zipCode?.value,
      city: this.city?.value,
      street: this.street?.value,
      houseNumber: this.houseNumber?.value,
      apartmentNumber: this.apartmentNumber?.value,
      phone: this.phone?.value,
      email: this.email?.value,
      emailForLabel: this.labelEmail?.value,
      pointId: this.pointId?.value,
      pointName: this.pointName?.value,
      pointZipCode: this.pointZipCode?.value,
      pointPartner: this.pointPartner?.value,
      pointCity: this.pointCity?.value,
      pointAddress: this.pointAddress?.value,
      pointDto: this.selectedPoint,
    };

    this.storeService.setShippingPointFormData(shippingPointFormData);
  }

  backButtonAction() {
    this.storeService.setSelectedPartners([]);
    this.navigationService.changeSposobNadaniaStepNumberTo(0);
    if (this.isPayerNoProvided()) {
      this.navigationService.changeScreenTo(ScreenType.OPCJE_DORECZENIA);
    } else {
      this.navigationService.changeScreenTo(ScreenType.STRONA_GLOWNA);
    }
  }

  async onSubmit() {
    this.isSubmitLoading.set(true);

    this.storeService.setShippingCourierFormData(undefined);

    try {
      if (this.pointForm.invalid) {
        this.formService.markAllAsTouched(this.pointForm);
        return;
      }

      if (this.isPayerNoProvided()) {
        try {
          const cityZipCodeResponse = await firstValueFrom(
            this.apiService.submitCityZipCodeRequest({
              zipCode: this.zipCode?.value,
              city: this.city?.value,
            })
          );
        } catch (error) {
          this.handleCityZipCodeError(error);
          return;
        }
      }

      try {
        if (this.isPayerNoProvided()) {
          const pickupData: PickupData = this.pickupService.buildPickupData();

          const pickupResponse = await firstValueFrom(
            this.apiService.submitPickupRequest(pickupData)
          );

          this.storeService.setPickupResponse(pickupResponse);
        }
        this.storeService.setSelectedPartners([]);
        this.navigationService.changeSposobNadaniaStepNumberTo(1);
        this.navigationService.changeScreenTo(ScreenType.OPCJE_NADANIA);
      } catch (error) {
        this.handlePickupError(error as HttpErrorResponse);
      }
    } finally {
      this.isSubmitLoading.set(false);
    }
  }

  handleCityZipCodeError(error: any) {
    try {
      const errorResponse: ErrorResponse = {
        httpStatus: error.status,
        errorMsg: error.error.errorMsg.map((msg: any) => ({
          errorCode: msg.errorCode,
          field: msg.field,
          idRow: msg.idRow,
          errorMsgLabel: msg.errorMsgLabel,
          errorMsgArgs: msg.errorMsgArgs,
        })),
      };
      if (errorResponse?.errorMsg?.[0]?.errorCode === 'FORBIDDEN') {
        this.captchaDialogService.openCaptchaDialog();
      } else if (errorResponse?.errorMsg?.[0]?.errorCode === 'INCONSISTENT') {
        this.zipCode?.setErrors({ zip_code_city_inconsistent: true });
        this.city?.setErrors({ zip_code_city_inconsistent: true });
      } else {
        this.submitException.set(true);
      }
    } catch (error) {
      this.submitException.set(true);
    }
  }

  handlePickupError(error: HttpErrorResponse) {
    try {
      const errorResponse: ErrorResponse = {
        httpStatus: error.status,
        errorMsg: error.error.errorMsg.map((msg: any) => ({
          errorCode: msg.errorCode,
          field: msg.field,
          idRow: msg.idRow,
          errorMsgLabel: msg.errorMsgLabel,
          errorMsgArgs: msg.errorMsgArgs,
        })),
      };

      if (errorResponse?.errorMsg?.[0]?.errorCode === 'FORBIDDEN') {
        this.captchaDialogService.openCaptchaDialog();
      } else if (errorResponse.httpStatus !== 400) {
        this.submitException.set(true);
      } else {
        this.storeService.setPickupErrorResponse(errorResponse);
        let backToCreateShip = false;
        let backToShippingOptions = false;
        let backToDeliveryOptions = false;

        const createShipFields = [
          'items.quantity',
          'item.length',
          'item.height',
          'item.compWeight',
          'item.width+item.length',
          'INS_VALUE',
          'INS_PCK_CONTENT',
          'COD_VALUE',
          'COD_ACCOUNT',
        ];
        const shippingOptionsFields: string[] = ['emailForLabel'];
        const deliveryOptionsFields = ['pckDate', 'pckInfo'];
        const shippingAndDeliveryFields = [
          'name',
          'companyName',
          'city',
          'address1',
          'phone',
          'email',
          'postalCode',
        ];
        const pointFields = ['ext2Id'];

        errorResponse?.errorMsg?.forEach((err) => {
          if (createShipFields.includes(err.field)) {
            backToCreateShip = true;
          }
          if (shippingOptionsFields.includes(err.field)) {
            backToShippingOptions = true;
          }
          if (deliveryOptionsFields.includes(err.field)) {
            backToDeliveryOptions = true;
          }
          if (shippingAndDeliveryFields.includes(err.field)) {
            if (err.idRow === 2) {
              backToDeliveryOptions = true;
            } else if (err.idRow === 1) {
              backToShippingOptions = true;
            }
          }
          if (pointFields.includes(err.field)) {
            if (err.idRow === 2 || err.idRow === 3)
              backToDeliveryOptions = true;
            else if (err.idRow === 1) backToShippingOptions = true;
          }
        });

        if (backToCreateShip) {
          this.navigationService.changeSposobNadaniaStepNumberTo(0);
          this.navigationService.changeScreenTo(ScreenType.UTWORZ_PRZESYLKE);
        } else if (backToDeliveryOptions) {
          this.navigationService.changeSposobNadaniaStepNumberTo(0);
          this.navigationService.changeScreenTo(ScreenType.OPCJE_DORECZENIA);
        } else if (backToShippingOptions) {
          this.ngAfterViewInit();
        } else {
          this.submitException.set(true);
        }
      }
    } catch (e) {
      this.submitException.set(true);
    }
    this.isSubmitLoading.set(false);
  }

  onInputChanged(
    event: Event,
    childComponent: FormPointInputFieldComponent
  ): void {
    const inputValue = (event.target as HTMLInputElement).value;
    childComponent.onInputChange(inputValue);
  }

  onInputFocus(
    focusValue: boolean,
    childComponent: FormPointInputFieldComponent
  ) {
    if (focusValue === false) {
      childComponent.onInputChange('');
    }
    childComponent.onInputFocus(focusValue);
  }

  onSelectedPoint(point: PointDto): void {
    this.selectedPoint = point;
    this.pointName?.setValue(point.pointName);
    this.pointId?.setValue(point.pointCode);
    this.pointZipCode?.setValue(point.postalCode);
    this.pointPartner?.setValue(point.partnerName);
    this.pointCity?.setValue(point.city);
    this.pointAddress?.setValue(point.address1);
  }

  isTooltipVisible = false;

  showTooltip(): void {
    this.isTooltipVisible = true;
  }

  hideTooltip(): void {
    this.isTooltipVisible = false;
  }

  toggleSelection(partnerName: string): void {
    this.selectedPartners$.pipe(take(1)).subscribe((selectedPartners) => {
      const current = selectedPartners ?? [];
      const isAlreadySelected = current.includes(partnerName);

      const updatedSelected = isAlreadySelected
        ? current.filter((name) => name !== partnerName)
        : [...current, partnerName];

      this.storeService.setSelectedPartners(updatedSelected);
    });
  }
}
